const TAB_SCOPED_KEYS = [
  "tokenLogin",
  "restaurantId",
  "restaurantCurrency",
  "restaurantSecondaryCurrency",
  "userRole",
  "franchiseFK",
  "email",
  "isOwner",
  "userPermissions",
  "userDetails",
];
const AUTH_SCOPE_KEY = "authSessionScope";
const AUTH_SESSION_TYPE_KEY = "authSessionType";
const TAB_SCOPE = "tab";

export const AUTH_SESSION_FRANCHISE_PORTAL = "franchise-portal";
export const AUTH_SESSION_RESTAURANT_IMPERSONATION = "restaurant-impersonation";

let bootstrappedImpersonationToken = null;

export function isTabScopedAuthSession() {
  return sessionStorage.getItem(AUTH_SCOPE_KEY) === TAB_SCOPE;
}

export function getAuthSessionType() {
  return sessionStorage.getItem(AUTH_SESSION_TYPE_KEY);
}

export function isFranchisePortalAuthSession() {
  return (
    isTabScopedAuthSession() &&
    getAuthSessionType() === AUTH_SESSION_FRANCHISE_PORTAL
  );
}

export function isRestaurantImpersonationAuthSession() {
  return (
    isTabScopedAuthSession() &&
    getAuthSessionType() === AUTH_SESSION_RESTAURANT_IMPERSONATION
  );
}

export function getAuthToken() {
  if (isTabScopedAuthSession()) {
    return sessionStorage.getItem("tokenLogin");
  }
  return localStorage.getItem("tokenLogin");
}

export function getRestaurantId() {
  if (isTabScopedAuthSession()) {
    return sessionStorage.getItem("restaurantId") || "";
  }
  return localStorage.getItem("restaurantId") || "";
}

export function getTokenRole(token) {
  if (!token) return null;
  try {
    const decoded = decodeTokenPayload(token);
    return decoded?.role || null;
  } catch {
    return null;
  }
}

export function decodeTokenPayload(token) {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

export function setTabAuthMeta(meta = {}) {
  Object.entries(meta).forEach(([key, value]) => {
    if (!TAB_SCOPED_KEYS.includes(key)) return;
    if (value === undefined || value === null || value === "") {
      sessionStorage.removeItem(key);
      return;
    }
    sessionStorage.setItem(key, String(value));
  });
}

export function beginTabScopedAuthSession(token, meta = {}) {
  sessionStorage.setItem(AUTH_SCOPE_KEY, TAB_SCOPE);
  if (token) {
    sessionStorage.setItem("tokenLogin", token);
  }
  if (meta && Object.keys(meta).length > 0) {
    setTabAuthMeta(meta);
  } else if (token) {
    const role = getTokenRole(token);
    if (role) {
      sessionStorage.setItem("userRole", role);
    }
  }
}

export function beginFranchisePortalSession(token, meta = {}) {
  sessionStorage.setItem(AUTH_SESSION_TYPE_KEY, AUTH_SESSION_FRANCHISE_PORTAL);
  beginTabScopedAuthSession(token, {
    ...meta,
    userRole: "resFranchise",
    restaurantId: "",
    restaurantCurrency: "",
    restaurantSecondaryCurrency: "",
  });
}

export function beginRestaurantImpersonationSession(token, restaurantId, meta = {}) {
  sessionStorage.setItem(
    AUTH_SESSION_TYPE_KEY,
    AUTH_SESSION_RESTAURANT_IMPERSONATION
  );

  const decoded = decodeTokenPayload(token);
  const resolvedRestaurantId =
    restaurantId || decoded?.restaurantId || decoded?.restaurantFK || "";

  beginTabScopedAuthSession(token, {
    ...meta,
    userRole: decoded?.role || getTokenRole(token) || "responsable",
    franchiseFK: "",
    restaurantId: resolvedRestaurantId,
    email: decoded?.email || meta.email || "",
  });
}

export function endTabScopedAuthSession() {
  TAB_SCOPED_KEYS.forEach((key) => sessionStorage.removeItem(key));
  sessionStorage.removeItem(AUTH_SCOPE_KEY);
  sessionStorage.removeItem(AUTH_SESSION_TYPE_KEY);
}

export function getSuperadminTokenFromUrl() {
  return new URLSearchParams(window.location.search).get("token");
}

export function installAuthTokenBridge() {
  if (window.__authTokenBridgeInstalled) return;
  window.__authTokenBridgeInstalled = true;

  const originalGetItem = localStorage.getItem.bind(localStorage);
  const originalSetItem = localStorage.setItem.bind(localStorage);
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);

  localStorage.getItem = (key) => {
    // Tab sessions must not fall back to the shared localStorage token.
    // Each superadmin "view as admin" tab keeps its own restaurant login.
    if (isTabScopedAuthSession() && TAB_SCOPED_KEYS.includes(key)) {
      return sessionStorage.getItem(key);
    }
    return originalGetItem(key);
  };

  localStorage.setItem = (key, value) => {
    if (isTabScopedAuthSession() && TAB_SCOPED_KEYS.includes(key)) {
      sessionStorage.setItem(key, value);
      return;
    }
    originalSetItem(key, value);
  };

  localStorage.removeItem = (key) => {
    if (isTabScopedAuthSession() && TAB_SCOPED_KEYS.includes(key)) {
      sessionStorage.removeItem(key);
      return;
    }
    originalRemoveItem(key);
  };
}

export function getBootstrappedImpersonationToken() {
  return bootstrappedImpersonationToken;
}

function bootstrapAuthFromUrl() {
  const { pathname } = window.location;
  // Password-reset / invite links also use ?token= — never treat those as impersonation.
  if (
    pathname.startsWith("/reset-password") ||
    pathname.startsWith("/accept-franchise-invite") ||
    pathname.startsWith("/accept-restaurant-join") ||
    pathname.startsWith("/activate-account") ||
    pathname.startsWith("/login")
  ) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  if (!token) return null;

  // Impersonation entry includes _imp and/or restaurantId
  // (franchise portal and superadmin "view as admin").
  const restaurantId = params.get("restaurantId") || "";
  const isImpersonation = Boolean(params.get("_imp") || restaurantId);
  if (!isImpersonation) return null;

  bootstrappedImpersonationToken = token;

  beginRestaurantImpersonationSession(token, restaurantId);

  const { hash } = window.location;
  params.delete("token");
  params.delete("restaurantId");
  params.delete("_imp");
  const query = params.toString();
  const nextUrl = query ? `${pathname}?${query}${hash}` : `${pathname}${hash}`;
  window.history.replaceState({}, "", nextUrl);

  return token;
}

if (typeof window !== "undefined") {
  installAuthTokenBridge();
  bootstrapAuthFromUrl();
  migrateLegacyFranchiseAuthToTabSession();
}

function migrateLegacyFranchiseAuthToTabSession() {
  if (isTabScopedAuthSession()) return;
  if (localStorage.getItem("userRole") !== "resFranchise") return;

  const token = localStorage.getItem("tokenLogin");
  if (!token) return;

  beginFranchisePortalSession(token, {
    email: localStorage.getItem("email") || "",
    franchiseFK: localStorage.getItem("franchiseFK") || "",
  });

  ["tokenLogin", "userRole", "franchiseFK", "email"].forEach((key) =>
    localStorage.removeItem(key)
  );
}
